'use client';

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useToast } from './ToastContext';
import axios from 'axios';
import { loginUser, registerUser, resetPassword as sendResetEmail, changeUserPassword } from '../services/authService';
import API_BASE_URL from '../config/api';

const AppContext = createContext();

// Safe localStorage operations
const safeLocalStorage = {
  getItem: (key) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.warn('localStorage getItem failed:', error);
    }
    return null;
  },
  setItem: (key, value) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('localStorage setItem failed:', error);
    }
  },
  removeItem: (key) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn('localStorage removeItem failed:', error);
    }
  }
};

const getFriendlyAuthErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred.';
  
  const code = error.code || (error.message && error.message.includes('auth/') 
    ? error.message.match(/auth\/[a-zA-Z0-9-]+/)?.[0] 
    : null);

  switch (code) {
    case 'auth/invalid-credential':
      return 'Invalid email address or password. Please check your credentials and try again.';
    case 'auth/user-not-found':
      return 'No account found with this email. Please register first.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'This email address is already registered. Try logging in instead.';
    case 'auth/weak-password':
      return 'Password is too weak. It must be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed login attempts. This account has been temporarily locked. Please try again later.';
    case 'auth/network-request-failed':
      return 'Could not connect to the authentication server. Please check your internet connection.';
    case 'auth/user-disabled':
      return 'This account has been suspended. Please contact support.';
    default:
      if (error.message && error.message.startsWith('Firebase:')) {
        return error.message.replace(/Firebase:\s*Error\s*\((auth\/[a-zA-Z0-9-]+)\)\.?/, '$1').trim();
      }
      return error.message || 'Authentication failed. Please try again.';
  }
};

const initialState = {
  user: null,
  token: safeLocalStorage.getItem('token'),
  location: JSON.parse(safeLocalStorage.getItem('location') || 'null'),
  cart: { items: [], totalAmount: 0 },
  wishlist: [],
  currency: safeLocalStorage.getItem('currency') || 'INR',
  rates: {
    INR: 1,
    USD: 0.012,
    EUR: 0.011,
    GBP: 0.0095,
    CAD: 0.016,
    AUD: 0.018,
    SGD: 0.016
  },
  loading: false,
  error: null
};

const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_USER':
      // If user has address, sync it to location state
      let userLocation = state.location;
      if (action.payload && action.payload.address && action.payload.address.zipCode) {
        userLocation = {
          pincode: action.payload.address.zipCode,
          city: action.payload.address.city,
          state: action.payload.address.state
        };
        safeLocalStorage.setItem('location', JSON.stringify(userLocation));
      }
      return { ...state, user: action.payload, location: userLocation, loading: false };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'LOGOUT':
      safeLocalStorage.removeItem('token');
      // We might want to keep location even after logout, or clear it. 
      // Let's keep it for better UX, or revert to localStorage value if it was overwritten
      return { ...state, user: null, token: null, cart: { items: [], totalAmount: 0 }, wishlist: [] };
    case 'SET_CART':
      const validCart = (action.payload && typeof action.payload === 'object' && Array.isArray(action.payload.items))
        ? action.payload
        : { items: [], totalAmount: 0 };
      return { ...state, cart: validCart };
    case 'SET_WISHLIST':
      const validWishlist = Array.isArray(action.payload) ? action.payload : [];
      return { ...state, wishlist: validWishlist };
    case 'SET_CURRENCY':
      safeLocalStorage.setItem('currency', action.payload);
      return { ...state, currency: action.payload };
    case 'SET_RATES':
      return { ...state, rates: action.payload };
    case 'SET_LOCATION':
      return { ...state, location: action.payload };
    default:
      return state;
  }
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { showToast } = useToast();

  // Set axios default headers
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common['x-auth-token'] = state.token;
      safeLocalStorage.setItem('token', state.token);
    } else {
      delete axios.defaults.headers.common['x-auth-token'];
      safeLocalStorage.removeItem('token');
    }
  }, [state.token]);

  const loadCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/cart`);
      const cartData = (res.data && typeof res.data === 'object' && Array.isArray(res.data.items))
        ? res.data
        : { items: [], totalAmount: 0 };
      dispatch({ type: 'SET_CART', payload: cartData });
    } catch (error) {
      console.error('Error loading cart:', error);
      dispatch({ type: 'SET_CART', payload: { items: [], totalAmount: 0 } });
    }
  }, []);

  const loadWishlist = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/wishlist`);
      const wishlistData = Array.isArray(res.data) ? res.data : [];
      dispatch({ type: 'SET_WISHLIST', payload: wishlistData });
    } catch (error) {
      console.error('Error loading wishlist:', error);
      dispatch({ type: 'SET_WISHLIST', payload: [] });
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const res = await axios.get(`${API_BASE_URL}/api/auth/profile`);
      dispatch({ type: 'SET_USER', payload: res.data });
      loadCart();
      loadWishlist();
    } catch (error) {
      console.error('Error loading user:', error);
      if (error.response && error.response.status === 401) {
        dispatch({ type: 'LOGOUT' });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [loadCart, loadWishlist]);

  // Load user on app start
  useEffect(() => {
    if (state.token) {
      loadUser();
    }
  }, [state.token, loadUser]);

  const login = useCallback(async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null }); // Clear previous errors
      const data = await loginUser(email, password);
      
      // Save token immediately so subsequent requests (loadCart/loadWishlist) succeed
      if (data.token) {
        safeLocalStorage.setItem('token', data.token);
        axios.defaults.headers.common['x-auth-token'] = data.token;
      }
      
      dispatch({ type: 'SET_TOKEN', payload: data.token });
      dispatch({ type: 'SET_USER', payload: data.user });
      loadCart();
      loadWishlist();
      showToast(`Welcome back, ${data.user.firstName}!`, 'success');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const message = getFriendlyAuthErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: message });
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [loadCart, loadWishlist, showToast]);

  const register = useCallback(async (userData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null }); // Clear previous errors
      const data = await registerUser({
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName
      });
      
      // Save token immediately so subsequent requests (loadCart/loadWishlist) succeed
      if (data.token) {
        safeLocalStorage.setItem('token', data.token);
        axios.defaults.headers.common['x-auth-token'] = data.token;
      }
      
      dispatch({ type: 'SET_TOKEN', payload: data.token });
      dispatch({ type: 'SET_USER', payload: data.user });
      loadCart();
      loadWishlist();
      showToast('Registration successful! Welcome to Rangaara.', 'success');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      const message = getFriendlyAuthErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: message });
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [loadCart, loadWishlist, showToast]);

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
    showToast('You have been logged out.', 'info');
  }, [showToast]);

  const resetPassword = useCallback(async (email) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      await sendResetEmail(email);
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast('Password reset link sent! Check your inbox.', 'success');
      return { success: true };
    } catch (error) {
      console.error('Reset password error:', error);
      const message = getFriendlyAuthErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: message });
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [showToast]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      await changeUserPassword(currentPassword, newPassword);
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast('Password changed successfully!', 'success');
      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      const message = getFriendlyAuthErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: message });
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [showToast]);

  const addToCart = useCallback(async (productId, quantity = 1, size, color) => {
    try {
      const token = safeLocalStorage.getItem('token') || safeLocalStorage.getItem('adminToken');
      const config = token ? { headers: { 'x-auth-token': token } } : {};

      const res = await axios.post(`${API_BASE_URL}/api/cart/add`, {
        productId,
        quantity,
        size,
        color
      }, config);
      dispatch({ type: 'SET_CART', payload: res.data });
      showToast('Added to cart successfully!', 'success');
      return { success: true };
    } catch (error) {
      console.error('addToCart error:', error);
      let message = error.response?.data?.message;
      if (!message) {
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          message = 'Unable to reach the server. Please check your connection or try again in a few moments.';
        } else {
          message = error.message || 'Failed to add item to cart';
        }
      }
      dispatch({ type: 'SET_ERROR', payload: message });
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [showToast]);

  const addToWishlist = useCallback(async (productId) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/wishlist/add/${productId}`);
      dispatch({ type: 'SET_WISHLIST', payload: res.data });
      showToast('Added to wishlist!', 'success');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add to wishlist';
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [showToast]);

  const removeFromWishlist = useCallback(async (productId) => {
    try {
      const res = await axios.delete(`${API_BASE_URL}/api/wishlist/remove/${productId}`);
      dispatch({ type: 'SET_WISHLIST', payload: res.data });
      showToast('Removed from wishlist.', 'info');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to remove from wishlist';
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [showToast]);

  const subscribeNewsletter = useCallback(async (email) => {
    try {
      await axios.post(`${API_BASE_URL}/api/newsletter/subscribe`, { email });
      showToast('Successfully subscribed to newsletter!', 'success');
      return { success: true, message: 'Successfully subscribed to newsletter!' };
    } catch (error) {
      const message = error.response?.data?.message || 'Subscription failed';
      showToast(message, 'error');
      return { success: false, message };
    }
  }, [showToast]);

  const setCurrency = useCallback((currency) => {
    dispatch({ type: 'SET_CURRENCY', payload: currency });
  }, []);

  const fetchRates = useCallback(async () => {
    try {
      const CACHE_KEY = 'rangaara_currency_rates';
      const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
      
      const cached = safeLocalStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.rates && (Date.now() - parsed.timestamp < CACHE_TTL)) {
            dispatch({ type: 'SET_RATES', payload: parsed.rates });
            return;
          }
        } catch (e) {
          // Ignore cache parse error and fetch fresh
        }
      }

      // Using a free API for demonstration. In production, use a paid key.
      const res = await axios.get('https://open.er-api.com/v6/latest/INR');
      if (res.data && res.data.rates) {
        dispatch({ type: 'SET_RATES', payload: res.data.rates });
        safeLocalStorage.setItem(CACHE_KEY, JSON.stringify({
          rates: res.data.rates,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.warn('Failed to fetch real-time rates, using fallback:', error);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const formatPrice = useCallback((price) => {
    const currencySymbols = {
      INR: '₹',
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$',
      SGD: 'S$'
    };
    
    const rate = state.rates[state.currency] || 1;
    const convertedPrice = Math.round(price * rate);
    
    return `${currencySymbols[state.currency] || ''}${convertedPrice.toLocaleString()}`;
  }, [state.currency, state.rates]);

  const updateLocation = useCallback(async (locationData) => {
    try {
      // Always update local state
      dispatch({ type: 'SET_LOCATION', payload: locationData });
      safeLocalStorage.setItem('location', JSON.stringify(locationData));

      // If logged in, also update backend profile
      if (state.token) {
        const { pincode, city, state: locState } = locationData;
        await axios.put(`${API_BASE_URL}/api/auth/profile`, {
          address: {
            zipCode: pincode,
            city: city,
            state: locState,
            country: locationData.country || 'India'
          }
        });
        // Reload user to sync
        loadUser();
      }
      return { success: true };
    } catch (error) {
      console.error('Failed to update location:', error);
      showToast('Failed to update location', 'error');
      return { success: false, message: error.message };
    }
  }, [state.token, loadUser, showToast]);

  const updateUserProfile = useCallback(async (profileData) => {
    try {
      const res = await axios.put(`${API_BASE_URL}/api/auth/profile`, profileData);
      if (res.data) {
        dispatch({ type: 'SET_USER', payload: res.data });
      }
      return { success: true, data: res.data };
    } catch (error) {
      console.error('Failed to update profile:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to update profile';
      return { success: false, message: msg };
    }
  }, []);

  const clearCartState = useCallback(() => {
    dispatch({ type: 'SET_CART', payload: { items: [], totalAmount: 0 } });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const value = {
    ...state,
    login,
    register,
    logout,
    resetPassword,
    changePassword,
    loadCart,
    clearCartState,
    addToCart,
    loadWishlist,
    addToWishlist,
    removeFromWishlist,
    subscribeNewsletter,
    setCurrency,
    formatPrice,
    updateLocation,
    updateUserProfile,
    clearError
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};